import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import Icon from "@/components/Icon/Icon";
import MultiSelect from "@/components/MultiSelect";
import type { MultiSelectOption } from "@/components/MultiSelect/types";
import { LabelPlace } from "@/features/place/components/LabelPlace/LabelPlace";
import { apiClient } from "@/lib/api/client";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: string[];
  onChange: (value?: string[]) => void;
  placeholder?: string;
  allowEmpty?: boolean;
  inputWidth?: number | string;
  showPlaceLink?: boolean;
};

export const CFormPlaceMultiSelectItem = buildCFormItem<Props>(
  (props: Props) => {
    const {
      value = [],
      placeholder,
      onChange,
      allowEmpty = true,
      inputWidth,
      readonly,
      showPlaceLink = false,
      ...restProps
    } = props;

    // 選択された拠点の詳細情報を取得
    const { data: selectedPlaces } = useQuery({
      queryKey: ["placesData", value],
      queryFn: async () => {
        if (!value || value.length === 0) return [];

        const placePromises = value.map((placeId) =>
          apiClient.GET("/v2/places/{placeId}", {
            params: { path: { placeId } },
          }),
        );

        const results = await Promise.all(placePromises);
        return results.map((res) => res.data).filter((data) => data != null);
      },
      enabled: !!value && value.length > 0,
    });

    // 選択肢用の拠点一覧を取得
    const { data: allPlaces } = useQuery({
      queryKey: ["place-select"],
      queryFn: async () => {
        const res = await apiClient.GET("/v2/components/place-select", {
          params: { query: { pageSize: 100, freeText: "" } },
        });
        return res.data?.items ?? [];
      },
    });

    const options: MultiSelectOption[] = useMemo(() => {
      if (!allPlaces || !Array.isArray(allPlaces)) return [];
      return allPlaces.map((place) => ({
        value: place.id,
        label: place.name,
      }));
    }, [allPlaces]);

    const readComponent = (() => {
      if (!value || value.length === 0) return placeholder;

      if (!selectedPlaces || selectedPlaces.length === 0) return placeholder;

      // 複数項目を改行で表示
      return (
        <div className="space-y-1">
          {selectedPlaces.map((place) => (
            <div key={place.id} className="flex items-center gap-2">
              {showPlaceLink && place.id && (
                <Link
                  to="/places/$id"
                  target="_blank"
                  rel="noopener noreferrer"
                  params={{ id: place.id }}
                >
                  <Icon iconName="SquareArrowOutUpRightIcon" />
                </Link>
              )}
              <LabelPlace name={place.name ?? ""} avatarUrl={place.avatarUrl} />
            </div>
          ))}
        </div>
      );
    })();

    const writeComponent = (
      <div
        style={{
          width: inputWidth,
        }}
      >
        <MultiSelect
          options={options}
          value={value}
          onValueChange={(newValue) => {
            // allowEmptyがfalseの場合、少なくとも1つの選択を維持
            if (!allowEmpty && newValue.length === 0) {
              return;
            }
            onChange(newValue.length > 0 ? newValue : undefined);
          }}
          disabled={readonly}
        />
      </div>
    );

    return (
      <CFormItemBase
        {...restProps}
        readComponent={readComponent}
        writeComponent={writeComponent}
      />
    );
  },
);
